import { type Ref } from 'vue'
import type { CroppedResult, DocumentAdjustments, SelectedRegionId } from '../types'

export function useTransformations(
  croppedResults: Ref<CroppedResult[]>,
  originalCroppedResults: Ref<CroppedResult[]>,
  baseCroppedResults: Ref<CroppedResult[]>,
  pristineCroppedResults: Ref<CroppedResult[]>,
  selectedCroppedRegionId: Ref<SelectedRegionId>,
  rotation: Ref<number>,
  croppedResultsRotation: Ref<number>,
  adjustments: Ref<Record<string, DocumentAdjustments>>,
  trimAmounts: Ref<Record<string, number>>,
  draw: () => void,
  drawSelectedCroppedRegion: () => void,
  updateMainCanvasWithAllCrops: () => void
) {
  // Rotation functions
  function rotateLeft() {
    if (croppedResults.value.length > 0 && selectedCroppedRegionId.value) {
      rotateCroppedResults(-90)
    } else {
      rotation.value = (rotation.value - 90) % 360
      draw()
    }
  }

  function rotateRight() {
    if (croppedResults.value.length > 0 && selectedCroppedRegionId.value) {
      rotateCroppedResults(90)
    } else {
      rotation.value = (rotation.value + 90) % 360
      draw()
    }
  }

  async function rotateCroppedResults(degrees: number) {
    // Update rotation tracking
    croppedResultsRotation.value = (croppedResultsRotation.value + degrees) % 360

    if (selectedCroppedRegionId.value === 'all') {
      // Rotate all cropped results
      const rotationPromises = croppedResults.value.map(async (result) => {
        result.imageData = await rotateImageData(result.imageData, degrees)
      })
      await Promise.all(rotationPromises)

      // Update original results to include rotation (new base state for adjustments)
      const originalRotationPromises = originalCroppedResults.value.map(async (result) => {
        result.imageData = await rotateImageData(result.imageData, degrees)
      })
      await Promise.all(originalRotationPromises)

      // Also update base results to include rotation (new base state for trims)
      const baseRotationPromises = baseCroppedResults.value.map(async (result) => {
        result.imageData = await rotateImageData(result.imageData, degrees)
      })
      await Promise.all(baseRotationPromises)

      // Update canvas to show all rotated results
      updateMainCanvasWithAllCrops()
    } else if (selectedCroppedRegionId.value) {
      // Rotate only the selected result
      const selectedResult = croppedResults.value.find(
        (result) => result.regionId === selectedCroppedRegionId.value
      )
      const originalResult = originalCroppedResults.value.find(
        (result) => result.regionId === selectedCroppedRegionId.value
      )
      const baseResult = baseCroppedResults.value.find(
        (result) => result.regionId === selectedCroppedRegionId.value
      )

      if (selectedResult) {
        selectedResult.imageData = await rotateImageData(selectedResult.imageData, degrees)
      }
      if (originalResult) {
        originalResult.imageData = await rotateImageData(originalResult.imageData, degrees)
      }
      if (baseResult) {
        baseResult.imageData = await rotateImageData(baseResult.imageData, degrees)
      }

      // Update canvas to show the rotated selected result
      drawSelectedCroppedRegion()
    }
  }

  function rotateImageData(imageDataUrl: string, degrees: number): Promise<string> {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(imageDataUrl)
        return
      }

      const img = new Image()
      img.onload = () => {
        // Set canvas size based on rotation
        if (degrees === 90 || degrees === -90 || degrees === 270) {
          canvas.width = img.height
          canvas.height = img.width
        } else {
          canvas.width = img.width
          canvas.height = img.height
        }

        // Apply rotation
        ctx.translate(canvas.width / 2, canvas.height / 2)
        ctx.rotate((degrees * Math.PI) / 180)
        ctx.drawImage(img, -img.width / 2, -img.height / 2)

        resolve(canvas.toDataURL('image/png', 1.0))
      }
      img.onerror = () => resolve(imageDataUrl)
      img.src = imageDataUrl
    })
  }

  // Adjustment functions
  async function applyCroppedResultAdjustments() {
    // Apply adjustments per page - each page can have its own adjustments
    const adjustmentPromises = croppedResults.value.map(async (result, index) => {
      const baseResult = baseCroppedResults.value[index]
      if (!baseResult) return

      // Check for adjustments: first individual page, then "all pages"
      const pageAdjustments = adjustments.value[result.regionId] || adjustments.value['all']

      if (
        !pageAdjustments ||
        (pageAdjustments.brightness === 0 &&
          pageAdjustments.contrast === 0 &&
          pageAdjustments.gamma === 0 &&
          pageAdjustments.sharpness === 0 &&
          pageAdjustments.saturation === 0)
      ) {
        // No adjustments for this page, restore to base state
        result.imageData = baseResult.imageData
      } else {
        // Apply adjustments for this page
        result.imageData = await adjustImageData(
          baseResult.imageData,
          Number(pageAdjustments.brightness),
          Number(pageAdjustments.contrast),
          Number(pageAdjustments.gamma),
          Number(pageAdjustments.sharpness),
          Number(pageAdjustments.saturation)
        )
      }
    })

    await Promise.all(adjustmentPromises)

    // Update canvas to show results
    if (selectedCroppedRegionId.value === 'all') {
      updateMainCanvasWithAllCrops()
    } else {
      drawSelectedCroppedRegion()
    }
  }

  function adjustImageData(
    imageDataUrl: string,
    brightnessValue: number,
    contrastValue: number,
    gammaValue: number,
    sharpnessValue: number,
    saturationValue: number
  ): Promise<string> {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(imageDataUrl)
        return
      }

      const img = new Image()
      img.onload = () => {
        canvas.width = img.width
        canvas.height = img.height

        // Draw the image
        ctx.drawImage(img, 0, 0)

        // Get image data for pixel manipulation
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const data = imageData.data

        // Apply document-focused adjustments
        const brightnessFactor = brightnessValue // -50 to +50
        const contrastFactor = 1 + contrastValue / 100 // 0.5 to 1.5
        const gammaAdjusted = 1 + gammaValue / 100 // 0.8 to 1.2
        const gammaCorrection = 1 / gammaAdjusted
        const saturationFactor = 1 + saturationValue / 100 // 0.5 to 1.5

        for (let i = 0; i < data.length; i += 4) {
          let r = data[i]
          let g = data[i + 1]
          let b = data[i + 2]

          // Apply brightness (additive)
          r += brightnessFactor
          g += brightnessFactor
          b += brightnessFactor

          // Apply contrast (around midpoint)
          r = (r - 128) * contrastFactor + 128
          g = (g - 128) * contrastFactor + 128
          b = (b - 128) * contrastFactor + 128

          // Apply gamma correction (for document readability)
          if (gammaValue !== 0) {
            r = 255 * Math.pow(r / 255, gammaCorrection)
            g = 255 * Math.pow(g / 255, gammaCorrection)
            b = 255 * Math.pow(b / 255, gammaCorrection)
          }

          // Apply saturation adjustment
          if (saturationValue !== 0) {
            // Convert RGB to HSL for saturation adjustment
            const rNorm = r / 255
            const gNorm = g / 255
            const bNorm = b / 255

            const max = Math.max(rNorm, gNorm, bNorm)
            const min = Math.min(rNorm, gNorm, bNorm)
            const lightness = (max + min) / 2

            if (max !== min) {
              const delta = max - min
              const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min)

              // Adjust saturation
              const newSaturation = Math.max(0, Math.min(1, saturation * saturationFactor))

              // Convert back to RGB
              const chroma = (1 - Math.abs(2 * lightness - 1)) * newSaturation
              const huePrime =
                max === rNorm
                  ? ((gNorm - bNorm) / delta) % 6
                  : max === gNorm
                    ? (bNorm - rNorm) / delta + 2
                    : (rNorm - gNorm) / delta + 4
              const x = chroma * (1 - Math.abs((huePrime % 2) - 1))
              const m = lightness - chroma / 2

              let rNew, gNew, bNew
              if (huePrime >= 0 && huePrime < 1) {
                rNew = chroma
                gNew = x
                bNew = 0
              } else if (huePrime >= 1 && huePrime < 2) {
                rNew = x
                gNew = chroma
                bNew = 0
              } else if (huePrime >= 2 && huePrime < 3) {
                rNew = 0
                gNew = chroma
                bNew = x
              } else if (huePrime >= 3 && huePrime < 4) {
                rNew = 0
                gNew = x
                bNew = chroma
              } else if (huePrime >= 4 && huePrime < 5) {
                rNew = x
                gNew = 0
                bNew = chroma
              } else {
                rNew = chroma
                gNew = 0
                bNew = x
              }

              r = (rNew + m) * 255
              g = (gNew + m) * 255
              b = (bNew + m) * 255
            }
          }

          // Clamp values to 0-255
          data[i] = Math.max(0, Math.min(255, r))
          data[i + 1] = Math.max(0, Math.min(255, g))
          data[i + 2] = Math.max(0, Math.min(255, b))
        }

        // Put the modified image data back
        ctx.putImageData(imageData, 0, 0)

        // Apply sharpness if needed (unsharp mask for text clarity)
        if (sharpnessValue > 0) {
          applySharpnessToCanvas(ctx, canvas.width, canvas.height, sharpnessValue)
        }

        resolve(canvas.toDataURL('image/png', 1.0))
      }
      img.onerror = () => resolve(imageDataUrl)
      img.src = imageDataUrl
    })
  }

  // Helper function to apply sharpness using unsharp mask
  function applySharpnessToCanvas(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    sharpnessValue: number
  ) {
    const imageData = ctx.getImageData(0, 0, width, height)
    const data = imageData.data
    const strength = sharpnessValue / 100 // 0 to 1

    // Simple unsharp mask implementation for text clarity
    // Create a copy of the original data
    const originalData = new Uint8ClampedArray(data)

    // Apply a simple sharpening kernel
    const kernel = [0, -1, 0, -1, 5, -1, 0, -1, 0]

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4

        for (let c = 0; c < 3; c++) {
          // RGB channels only
          let sum = 0
          for (let ky = -1; ky <= 1; ky++) {
            for (let kx = -1; kx <= 1; kx++) {
              const kidx = ((y + ky) * width + (x + kx)) * 4 + c
              const kernelIdx = (ky + 1) * 3 + (kx + 1)
              sum += originalData[kidx] * kernel[kernelIdx]
            }
          }

          // Blend with original based on strength
          const sharpened = Math.max(0, Math.min(255, sum))
          data[idx + c] = originalData[idx + c] + (sharpened - originalData[idx + c]) * strength
        }
      }
    }

    ctx.putImageData(imageData, 0, 0)
  }

  // Trim functions
  async function applyTrimEdges(regionId: string, trimPercentage: number) {
    // Store the trim amount
    trimAmounts.value[regionId] = trimPercentage

    // Handle original image trim (when no crop regions exist)
    if (regionId === 'original') {
      // Apply trim to the original image and redraw
      if (trimPercentage === 0) {
        // Reset to original image
        draw()
      } else {
        // Apply trim to original image and redraw
        draw()
      }
      return
    }

    // Handle "all" pages
    if (regionId === 'all') {
      // Apply trim to all base results (without adjustments)
      const trimPromises = baseCroppedResults.value.map(async (baseResult) => {
        const originalResult = originalCroppedResults.value.find(
          (r) => r.regionId === baseResult.regionId
        )
        if (!originalResult) return

        if (trimPercentage === 0) {
          // Reset to original (includes rotation but no trim)
          baseResult.imageData = originalResult.imageData
        } else {
          // Apply trim to the original image
          const trimmedImageData = await trimImageData(originalResult.imageData, trimPercentage)
          baseResult.imageData = trimmedImageData
        }
      })

      await Promise.all(trimPromises)

      // Now apply adjustments on top of the trimmed base images
      await applyCroppedResultAdjustments()

      // Update display
      updateMainCanvasWithAllCrops()
      return
    }

    // Handle individual page
    const baseResult = baseCroppedResults.value.find((r) => r.regionId === regionId)
    const originalResult = originalCroppedResults.value.find((r) => r.regionId === regionId)

    if (!baseResult || !originalResult) return

    if (trimPercentage === 0) {
      // Reset to original (includes rotation but no trim)
      baseResult.imageData = originalResult.imageData
    } else {
      // Apply trim to the original image
      const trimmedImageData = await trimImageData(originalResult.imageData, trimPercentage)
      baseResult.imageData = trimmedImageData
    }

    // Now apply adjustments on top of the trimmed base image
    await applyCroppedResultAdjustments()

    // Update display
    if (selectedCroppedRegionId.value === 'all') {
      updateMainCanvasWithAllCrops()
    } else {
      drawSelectedCroppedRegion()
    }
  }

  // Trim image data by cropping uniformly from all sides
  async function trimImageData(imageData: string, trimPercentage: number): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (!ctx) return resolve(imageData)

        // Calculate trim amounts
        const trimAmountX = (img.width * trimPercentage) / 200 // Divide by 200 to get half for each side
        const trimAmountY = (img.height * trimPercentage) / 200

        // Calculate new dimensions
        const newWidth = Math.max(50, img.width - trimAmountX * 2)
        const newHeight = Math.max(50, img.height - trimAmountY * 2)

        canvas.width = newWidth
        canvas.height = newHeight

        // Draw the trimmed portion
        ctx.drawImage(
          img,
          trimAmountX, // source x
          trimAmountY, // source y
          newWidth, // source width
          newHeight, // source height
          0, // dest x
          0, // dest y
          newWidth, // dest width
          newHeight // dest height
        )

        resolve(canvas.toDataURL())
      }
      img.src = imageData
    })
  }

  // Reset functions
  function resetRotation() {
    rotation.value = 0
    croppedResultsRotation.value = 0

    // If we have cropped results, restore base data and re-apply other transformations
    if (croppedResults.value.length > 0) {
      // Restore baseCroppedResults from pristine state (removes rotation)
      baseCroppedResults.value.forEach((result, index) => {
        if (pristineCroppedResults.value[index]) {
          result.imageData = pristineCroppedResults.value[index].imageData
        }
      })

      // Restore originalCroppedResults from pristine state (removes rotation)
      originalCroppedResults.value.forEach((result, index) => {
        if (pristineCroppedResults.value[index]) {
          result.imageData = pristineCroppedResults.value[index].imageData
        }
      })

      // Re-apply other transformations (adjustments) on top of the reset base
      applyCroppedResultAdjustments()
    } else {
      draw()
    }
  }

  function resetAdjustments() {
    const currentPageId = selectedCroppedRegionId.value || 'all'

    if (currentPageId === 'all') {
      // When "All pages" is selected, reset all adjustments
      adjustments.value = {}
    } else {
      // Reset adjustments for the current page only by removing them entirely
      if (adjustments.value[currentPageId]) {
        delete adjustments.value[currentPageId]
      }
    }

    // Apply the reset adjustments and update canvas
    if (croppedResults.value.length > 0 && selectedCroppedRegionId.value) {
      applyCroppedResultAdjustments()
    } else {
      draw()
    }
  }

  function resetTrim() {
    const currentPageId = selectedCroppedRegionId.value || 'all'

    if (currentPageId === 'all') {
      // When "All pages" is selected, reset all trim amounts
      trimAmounts.value = {}
    } else {
      // Reset trim for the current page only by removing it entirely
      if (trimAmounts.value[currentPageId]) {
        delete trimAmounts.value[currentPageId]
      }
    }

    // Re-apply transformations to restore base state without trim
    if (croppedResults.value.length > 0 && selectedCroppedRegionId.value) {
      // Reset base results to original (without trim)
      if (currentPageId === 'all') {
        baseCroppedResults.value.forEach((baseResult, index) => {
          const originalResult = originalCroppedResults.value[index]
          if (originalResult) {
            baseResult.imageData = originalResult.imageData
          }
        })
      } else {
        const baseResult = baseCroppedResults.value.find((r) => r.regionId === currentPageId)
        const originalResult = originalCroppedResults.value.find(
          (r) => r.regionId === currentPageId
        )
        if (baseResult && originalResult) {
          baseResult.imageData = originalResult.imageData
        }
      }

      // Re-apply adjustments on top of the reset base
      applyCroppedResultAdjustments()
    } else {
      draw()
    }
  }

  function resetTransformations() {
    // Restore original cropped results
    croppedResults.value = originalCroppedResults.value.map((result) => ({
      ...result,
      imageData: result.imageData // Create a copy
    }))

    // Reset transformation values
    rotation.value = 0
    croppedResultsRotation.value = 0
    adjustments.value = {}

    // Update canvas to show reset state
    if (selectedCroppedRegionId.value === 'all') {
      updateMainCanvasWithAllCrops()
    } else {
      drawSelectedCroppedRegion()
    }
  }

  // Helper functions
  function hasAnyAdjustments(): boolean {
    const currentPageId = selectedCroppedRegionId.value || 'all'
    const currentAdjustments = adjustments.value[currentPageId]

    if (!currentAdjustments) return false

    return (
      Number(currentAdjustments.brightness) !== 0 ||
      Number(currentAdjustments.contrast) !== 0 ||
      Number(currentAdjustments.gamma) !== 0 ||
      Number(currentAdjustments.sharpness) !== 0
    )
  }

  return {
    // Rotation
    rotateLeft,
    rotateRight,
    rotateCroppedResults,
    rotateImageData,

    // Adjustments
    applyCroppedResultAdjustments,
    adjustImageData,

    // Trim
    applyTrimEdges,
    trimImageData,

    // Reset
    resetRotation,
    resetAdjustments,
    resetTrim,
    resetTransformations,

    // Helpers
    hasAnyAdjustments
  }
}
